// components/views/TemplateLibraryView.jsx
import { useState, useRef } from 'react';
import { Modal, Callout, EmptyState, LoadRow, SectionLabel, Badge } from '../UI';
import { CONTRACT_TYPES, EXHIBIT_TAGS, uid, today, fmtDate, fmtSize } from '../../utils/constants';
import { templatesDb } from '../../utils/store';

// ─── Upload / Edit Modal ──────────────────────────────────────────────────
function TemplateModal({ initial, onSave, onCancel, loading, isAgencyAdmin = false }) {
  const [name, setName]   = useState(initial?.name || '');
  const [type, setType]   = useState(initial?.type || 'Consulting');
  const [exhibits, setExhibits] = useState(initial?.exhibits || ['A', 'B', 'D']);
  const [notes, setNotes] = useState(initial?.notes || '');
  const [file, setFile]   = useState(null);
  // Template type: canonical (agency's blessed standard) vs personal_reference
  // (analyst sample). Locked to personal_reference for non-admins per Step 2
  // gating. Defaults to existing value when editing, or personal_reference for
  // new uploads.
  const [referenceKind, setReferenceKind] = useState(initial?.reference_kind || 'personal_reference');
  const fileRef = useRef(null);

  function toggleExhibit(key) {
    setExhibits(p => {
      const next = p.includes(key) ? p.filter(k => k !== key) : [...p, key].sort();
      // If exhibits goes away from single-element, force back to personal_reference
      // since canonical requires exactly one exhibit.
      if (next.length !== 1 && referenceKind === 'canonical') {
        setReferenceKind('personal_reference');
      }
      return next;
    });
  }

  const canSave = name.trim() && (initial?.id ? true : file);

  return (
    <Modal
      title={initial?.id ? 'Edit Template' : 'Add Contract Template'}
      subtitle="Upload a sample contract PDF to use as a drafting guide for Exhibit B (Budget Detail & Payment Provisions) and Exhibit D (Special Terms and Conditions)"
      onClose={onCancel}
      size="lg"
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {/* Name */}
        <div className="form-field">
          <label>Template Name <span style={{ color: 'var(--red-500)' }}>*</span></label>
          <input value={name} onChange={e => setName(e.target.value)} placeholder="e.g., CPS HR Consulting 2024, IT Maintenance Services" />
        </div>

        {/* Contract type */}
        <div className="form-field">
          <label>Contract Type <span style={{ color: 'var(--red-500)' }}>*</span></label>
          <select value={type} onChange={e => setType(e.target.value)}>
            {CONTRACT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>

        {/* Template Type — canonical vs personal_reference. Locked for
            non-admins per the Step 2 gating policy. Also locked for
            multi-exhibit rows per the Step 2.5 single-exhibit-only rule:
            canonical is per-(agency, type, exhibit) so a row that covers
            multiple exhibits can't be designated canonical. Slice first. */}
        <div className="form-field">
          <label>
            Template Type
            {!isAgencyAdmin && (
              <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 400, color: 'var(--slate-400)', fontStyle: 'italic' }}>
                — Only agency admins can mark templates canonical
              </span>
            )}
            {isAgencyAdmin && exhibits.length !== 1 && (
              <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 400, color: 'var(--slate-400)', fontStyle: 'italic' }}>
                — Canonical requires exactly one exhibit. Use the slicer to split combined templates.
              </span>
            )}
          </label>
          <select
            value={referenceKind}
            onChange={e => setReferenceKind(e.target.value)}
            disabled={!isAgencyAdmin || exhibits.length !== 1}
            style={(!isAgencyAdmin || exhibits.length !== 1) ? { background: 'var(--slate-100)', color: 'var(--slate-500)', cursor: 'not-allowed' } : undefined}
          >
            <option value="personal_reference">Personal Reference — analyst sample, supplementary</option>
            <option value="canonical">👑 Canonical — agency's blessed standard for this exhibit</option>
          </select>
          {referenceKind === 'canonical' && isAgencyAdmin && exhibits.length === 1 && (
            <div style={{ marginTop: 6, padding: 8, background: 'var(--gold-50, #fefce8)', border: '1px solid var(--gold-200, #fde68a)', borderRadius: 6, fontSize: 11.5, color: 'var(--slate-600)', lineHeight: 1.5 }}>
              Marking this as canonical means Penny will prefer this template for <strong>{type}</strong> contracts when working on <strong>Exhibit {exhibits[0]}</strong>. If another canonical template exists for this same combination, it will be automatically demoted to Personal Reference.
            </div>
          )}
        </div>

        {/* Exhibits included */}
        <div className="form-field">
          <label>Exhibits Included in this PDF</label>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 6 }}>
            {EXHIBIT_TAGS.map(({ key, label }) => (
              <label key={key} style={{
                display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer',
                padding: '7px 14px', borderRadius: 'var(--r-md)',
                border: '1.5px solid ' + (exhibits.includes(key) ? 'var(--navy-500)' : 'var(--slate-200)'),
                background: exhibits.includes(key) ? 'var(--blue-50)' : 'var(--white)',
                fontSize: 12.5, userSelect: 'none',
              }}>
                <input type="checkbox" checked={exhibits.includes(key)} onChange={() => toggleExhibit(key)} style={{ margin: 0 }} />
                <span style={{ fontWeight: exhibits.includes(key) ? 600 : 400 }}>{label}</span>
              </label>
            ))}
          </div>
        </div>

        {/* PDF upload */}
        <div className="form-field">
          <label>{initial?.id ? 'Replace PDF (optional)' : 'Contract PDF'} <span style={{ color: 'var(--red-500)' }}>{initial?.id ? '' : '*'}</span></label>
          {initial?.id && !file && (
            <div style={{ fontSize: 12, color: 'var(--green-600)', marginBottom: 6 }}>
              ✓ Current: {initial.fileName} — leave blank to keep existing
            </div>
          )}
          <div
            className={`upload-zone ${file ? 'loaded' : ''}`}
            style={{ marginTop: 4 }}
            onClick={() => fileRef.current?.click()}
            onDragOver={e => e.preventDefault()}
            onDrop={e => { e.preventDefault(); const f = e.dataTransfer.files[0]; if (f) processFile(f); }}
          >
            {file
              ? <><div className="upload-zone-title">✓ {file.name}</div><div className="upload-zone-sub">Click to replace</div></>
              : <><div className="upload-zone-title">Click or drag contract PDF here</div><div className="upload-zone-sub">Upload a complete sample contract — Penny will use it as a template</div></>
            }
          </div>
          <input ref={fileRef} type="file" accept=".pdf" style={{ display: 'none' }}
            onChange={e => { if (e.target.files[0]) processFile(e.target.files[0]); e.target.value = ''; }}
          />
        </div>

        {/* Notes */}
        <div className="form-field">
          <label>Notes (optional)</label>
          <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2}
            placeholder="e.g., Used for HR consulting, includes milestone payment schedule, DGS approved"
          />
        </div>
      </div>

      {loading && <LoadRow msg="Uploading template…" />}

      <div className="btn-row">
        <button className="btn btn-gold" disabled={!canSave || loading}
          onClick={() => onSave({ name: name.trim(), type, exhibits, notes, file, reference_kind: referenceKind })}>
          {initial?.id ? 'Save Changes' : 'Add Template'}
        </button>
        <button className="btn btn-ghost" onClick={onCancel} disabled={loading}>Cancel</button>
      </div>
    </Modal>
  );

  function processFile(f) {
    const reader = new FileReader();
    reader.onload = ev => setFile({ name: f.name, base64: ev.target.result.split(',')[1], size: f.size });
    reader.readAsDataURL(f);
  }
}

// ─── Main View ────────────────────────────────────────────────────────────
export default function TemplateLibraryView({ templates, onUpdate, toast, profile, apiKey, callAI, uploadToAnthropicFiles }) {
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing]     = useState(null);
  const [saving, setSaving]       = useState(false);
  const [filterType, setFilterType] = useState('All');

  // Agency-admin gating for canonical promotion. Matches the role check used
  // elsewhere for agency-level config (PAAL, Term Clause, Exhibit B/D
  // templates). Analysts and supervisors can still upload templates but their
  // uploads are locked to personal_reference.
  const isAgencyAdmin = profile?.role === 'agency_super_admin';

  // ── Slicer Wizard state ──────────────────────────────────────────────
  // Wizard flow:
  //   slicerStep === null     → wizard closed
  //   slicerStep === 'upload' → drop a multi-exhibit PDF
  //   slicerStep === 'detect' → Penny is analyzing (loading)
  //   slicerStep === 'review' → show detected slices, let user edit/deselect
  //   slicerStep === 'saving' → writing each slice as a new template row
  const [slicerStep, setSlicerStep] = useState(null);
  const [slicerFile, setSlicerFile] = useState(null);          // { name, base64, size }
  const [slicerSlices, setSlicerSlices] = useState([]);        // detected exhibit slices
  const [slicerSourceName, setSlicerSourceName] = useState(''); // base name for slice titles
  const [slicerContractType, setSlicerContractType] = useState('Consulting'); // applies to all slices

  // Sort canonical templates to the top within each filter. Within each tier,
  // newest first (templates already arrive sorted by updated_at desc from
  // listVisible, so we preserve that order via a stable sort).
  const filtered = (filterType === 'All'
    ? templates
    : templates.filter(t => t.type === filterType)
  ).slice().sort((a, b) => {
    const aC = a.reference_kind === 'canonical' ? 1 : 0;
    const bC = b.reference_kind === 'canonical' ? 1 : 0;
    return bC - aC; // canonical (1) before personal_reference (0)
  });

  const typeCount = CONTRACT_TYPES.reduce((acc, t) => {
    acc[t] = templates.filter(tp => tp.type === t).length;
    return acc;
  }, {});

  async function handleSave(data) {
    setSaving(true);
    try {
      // Determine if this save involves a canonical promotion. Two cases:
      //   1. New upload with reference_kind=canonical
      //   2. Edit of existing template where reference_kind changed to canonical
      // (Demotion is also possible but uses a simpler path — just update the field.)
      const isPromotingToCanonical =
        isAgencyAdmin &&
        data.reference_kind === 'canonical' &&
        (!editing?.id || editing.reference_kind !== 'canonical');
      const isDemoting =
        isAgencyAdmin &&
        editing?.reference_kind === 'canonical' &&
        data.reference_kind === 'personal_reference';

      // Build the entry. Only include reference_kind on the entry when we're
      // NOT going to promote separately — promotion via templatesDb.save would
      // hit the unique-index conflict before our auto-swap could clear it.
      const baseEntry = editing?.id
        ? { ...editing, name: data.name, type: data.type, exhibits: data.exhibits, notes: data.notes, updatedAt: today(),
            ...(data.file ? { fileName: data.file.name, fileSize: data.file.size } : {}) }
        : { id: uid(), name: data.name, type: data.type, exhibits: data.exhibits, notes: data.notes,
            fileName: data.file?.name, fileSize: data.file?.size, createdAt: today() };
      const entry = isPromotingToCanonical
        ? baseEntry  // do NOT include reference_kind=canonical here
        : { ...baseEntry, reference_kind: data.reference_kind || 'personal_reference' };

      const ok = await templatesDb.save(entry, data.file?.base64 || null);
      if (!ok) throw new Error('Save failed');

      // If promoting, run the auto-swap promotion now that the row exists.
      if (isPromotingToCanonical) {
        const result = await templatesDb.promoteToCanonical(entry.id);
        if (!result.ok) {
          if (result.reason === 'multi_exhibit') {
            throw new Error('Canonical requires exactly one exhibit. This template has ' + (result.exhibitCount || 'multiple') + '. Slice it first.');
          }
          throw new Error('Promotion failed: ' + (result.reason || 'unknown'));
        }
        entry.reference_kind = 'canonical';
        if (result.swapped && result.demoted) {
          toast.info('Demoted previous canonical for Exhibit ' + result.exhibit + ': ' + (result.demoted.name || 'unnamed'));
        }
      }

      // Build the new templates list. If we promoted, also mark any
      // previously-canonical row (for the same type AND exhibit) as
      // personal_reference in local state so the UI reflects DB state.
      let updated = editing?.id
        ? templates.map(t => t.id === entry.id ? entry : t)
        : [entry, ...templates];
      if (isPromotingToCanonical && entry.exhibits?.length === 1) {
        const targetExhibit = entry.exhibits[0];
        updated = updated.map(t =>
          (t.id !== entry.id
            && t.type === entry.type
            && t.reference_kind === 'canonical'
            && Array.isArray(t.exhibits)
            && t.exhibits.length === 1
            && t.exhibits[0] === targetExhibit)
            ? { ...t, reference_kind: 'personal_reference' }
            : t
        );
      }

      onUpdate(updated);
      setShowModal(false);
      setEditing(null);
      const verb = isPromotingToCanonical
        ? 'promoted to canonical'
        : isDemoting
          ? 'demoted to personal reference'
          : (editing?.id ? 'updated' : 'added to library');
      toast.success(entry.name + ' ' + verb);
    } catch (e) {
      toast.error('Failed to save template: ' + e.message);
    } finally {
      setSaving(false);
    }
  }

  async function handleRemove(id) {
    const t = templates.find(tp => tp.id === id);
    await templatesDb.delete(id);
    onUpdate(templates.filter(tp => tp.id !== id));
    toast.info((t?.name || 'Template') + ' removed');
  }

  // ── Slicer Wizard ────────────────────────────────────────────────────
  // Takes a multi-exhibit PDF, asks Penny to detect exhibit boundaries,
  // lets the user confirm/adjust, then saves each slice as a separate
  // template row in `user_templates`. Each slice carries the FULL PDF
  // bytes (since we don't physically split the PDF — Penny is told to
  // look at specific pages when she references the template), but its
  // `exhibits` array contains only the single exhibit it represents.

  function openSlicer() {
    setSlicerStep('upload');
    setSlicerFile(null);
    setSlicerSlices([]);
    setSlicerSourceName('');
    setSlicerContractType('Consulting');
  }

  function closeSlicer() {
    setSlicerStep(null);
    setSlicerFile(null);
    setSlicerSlices([]);
  }

  // Read a File into base64 (strip data URI prefix)
  function readFileAsBase64(file) {
    return new Promise((resolve, reject) => {
      const r = new FileReader();
      r.onload = () => resolve(String(r.result).split(',')[1]);
      r.onerror = () => reject(new Error('Could not read file'));
      r.readAsDataURL(file);
    });
  }

  // Read a File into an ArrayBuffer (for JSZip)
  function readFileAsArrayBuffer(file) {
    return new Promise((resolve, reject) => {
      const r = new FileReader();
      r.onload = () => resolve(r.result);
      r.onerror = () => reject(new Error('Could not read file'));
      r.readAsArrayBuffer(file);
    });
  }

  // Extract paragraph text from a .docx file.
  // A .docx is a ZIP containing word/document.xml. We unzip, parse the XML,
  // and return an array of { index, text } — one entry per <w:p> paragraph.
  // The text is just the concatenated text of all <w:t> runs in that paragraph.
  // Returns { paragraphs, totalCount }.
  async function extractDocxParagraphs(arrayBuffer) {
    const JSZip = window.JSZip;
    if (!JSZip) {
      throw new Error('JSZip not loaded — required for docx parsing. Refresh the page.');
    }
    const zip = await JSZip.loadAsync(arrayBuffer);
    const docXmlFile = zip.file('word/document.xml');
    if (!docXmlFile) {
      throw new Error('Invalid .docx file (no word/document.xml).');
    }
    const xmlString = await docXmlFile.async('string');
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(xmlString, 'application/xml');

    // Each <w:p> is a paragraph. Inside, <w:t> elements hold the actual text.
    // Use getElementsByTagNameNS to handle the w: namespace properly.
    const W_NS = 'http://schemas.openxmlformats.org/wordprocessingml/2006/main';
    const paragraphNodes = xmlDoc.getElementsByTagNameNS(W_NS, 'p');
    const paragraphs = [];
    for (let i = 0; i < paragraphNodes.length; i++) {
      const pNode = paragraphNodes[i];
      const textNodes = pNode.getElementsByTagNameNS(W_NS, 't');
      let text = '';
      for (let j = 0; j < textNodes.length; j++) {
        text += textNodes[j].textContent || '';
      }
      paragraphs.push({ index: i, text: text.trim() });
    }
    return { paragraphs, totalCount: paragraphs.length };
  }

  async function handleSlicerFilePicked(file) {
    if (!file) return;
    const isPdf  = file.type === 'application/pdf' || /\.pdf$/i.test(file.name);
    const isDocx = file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' || /\.docx$/i.test(file.name);
    if (!isPdf && !isDocx) {
      toast.error('Please pick a PDF or .docx file');
      return;
    }
    if (!callAI || !uploadToAnthropicFiles) {
      toast.error('AI tools unavailable — try again later');
      return;
    }
    try {
      const base64 = await readFileAsBase64(file);
      const kind = isPdf ? 'pdf' : 'docx';
      setSlicerFile({ name: file.name, base64, size: file.size, kind });
      // Suggest a base name from the filename (without extension)
      const baseName = file.name.replace(/\.(pdf|docx)$/i, '').replace(/[_-]+/g, ' ').trim();
      setSlicerSourceName(baseName || 'Multi-exhibit template');

      // Kick off Penny detection — different paths for pdf vs docx
      setSlicerStep('detect');
      let detected;
      if (isPdf) {
        detected = await runSlicerDetectionPdf(base64, file.name);
      } else {
        // For docx we need to extract paragraphs locally first
        const arrayBuffer = await readFileAsArrayBuffer(file);
        const { paragraphs, totalCount } = await extractDocxParagraphs(arrayBuffer);
        if (paragraphs.length === 0) {
          throw new Error('The .docx file has no readable paragraphs.');
        }
        detected = await runSlicerDetectionDocx(paragraphs, file.name, totalCount);
      }
      setSlicerSlices(detected);
      setSlicerStep('review');
    } catch (err) {
      console.error('Slicer error:', err);
      toast.error('Detection failed: ' + err.message);
      setSlicerStep('upload');
    }
  }

  // PDF detection path — give Penny the PDF directly via Files API
  async function runSlicerDetectionPdf(base64, fileName) {
    // Upload to Anthropic Files API so Penny can read the PDF directly
    const fileId = await uploadToAnthropicFiles(apiKey, base64, fileName);

    const sys = `You are Penny, the GoldenScope contracting assistant. The user uploaded a multi-exhibit California state contract document. Identify each exhibit (typically labeled "EXHIBIT A", "EXHIBIT B", "EXHIBIT D", etc., often with subtitles like "SCOPE OF WORK" or "BUDGET DETAIL AND PAYMENT PROVISIONS") and return the page range of each.

Return ONLY a JSON object — no preamble, no markdown, no code fences:
{
  "slices": [
    {
      "exhibit": "A" | "B" | "C" | "D" | "E",
      "suggested_title": "<short descriptive title for this exhibit, e.g. 'Scope of Work — Professional Services'>",
      "page_start": <1-indexed page number where this exhibit begins>,
      "page_end": <1-indexed page number where this exhibit ends>,
      "confidence": "high" | "medium" | "low"
    }
  ]
}

Rules:
1. Page numbers are 1-indexed (first page = 1).
2. An exhibit's page_end is the last page that belongs to it (inclusive). If Exhibit B starts on page 9 and Exhibit D starts on page 13, then Exhibit B's page_end is 12.
3. If you see a "Standard Agreement" (STD 213) form before any exhibits, do NOT include it as a slice. Only include exhibits that have explicit "EXHIBIT X" labels.
4. Skip front matter (table of contents, agreement signature pages, transmittal letters).
5. Confidence is "high" when the exhibit boundaries are crystal clear, "medium" when there's some ambiguity, "low" if you're guessing.
6. Order slices by page_start ascending.

If the document doesn't contain identifiable exhibits, return { "slices": [] }.`;

    const userText = `Identify all exhibits in this PDF and return their page ranges per the instructions. Return JSON only.`;

    const raw = await callAI(apiKey, {
      system: sys,
      model: 'claude-sonnet-4-6',
      max_tokens: 1500,
      messages: [{
        role: 'user',
        content: [
          { type: 'document', source: { type: 'file', file_id: fileId } },
          { type: 'text', text: userText },
        ],
      }],
      tag: 'slicer_detect',
    });

    const cleaned = String(raw || '').trim().replace(/^```(?:json)?\s*/i, '').replace(/```\s*$/i, '').trim();
    let parsed;
    try {
      parsed = JSON.parse(cleaned);
    } catch (e) {
      console.error('Slicer detection JSON parse failed:', e, 'raw:', cleaned);
      throw new Error('Penny returned an unparseable response. Try a different PDF.');
    }
    if (!Array.isArray(parsed?.slices) || parsed.slices.length === 0) {
      throw new Error('No exhibits detected. Check that the PDF has explicit "EXHIBIT X" labels.');
    }
    // Normalize + default include=true
    return parsed.slices.map((s, i) => ({
      id: 'slice-' + i,
      exhibit: String(s.exhibit || '').toUpperCase().replace(/[^A-E]/g, ''),
      suggested_title: String(s.suggested_title || ''),
      // Normalized fields work for both PDF (pages) and docx (paragraphs).
      // The UI renders the label dynamically based on slicerFile.kind.
      start: parseInt(s.page_start, 10) || 1,
      end: parseInt(s.page_end, 10) || 1,
      confidence: ['high', 'medium', 'low'].includes(String(s.confidence)) ? String(s.confidence) : 'medium',
      include: true,
    })).filter(s => s.exhibit); // drop any with empty exhibit
  }

  // Docx detection path — extract paragraphs locally, send numbered text to Penny.
  // Penny returns paragraph_start / paragraph_end indices (0-based) rather than
  // page numbers, since docx files have no page concept until rendered.
  async function runSlicerDetectionDocx(paragraphs, fileName, totalCount) {
    // Build a numbered representation Penny can scan. Skip empty paragraphs
    // in the prompt to save tokens (but keep them in the actual index space —
    // their indices remain stable so user edits work correctly).
    // We cap text length per paragraph so a 200-page contract doesn't blow
    // the context window.
    const lines = paragraphs
      .map(p => {
        const txt = (p.text || '').slice(0, 200);
        if (!txt) return null;
        return '[' + p.index + '] ' + txt;
      })
      .filter(Boolean)
      .join('\n');

    const sys = `You are Penny, the GoldenScope contracting assistant. The user uploaded a multi-exhibit California state contract Word document. The document has been extracted as numbered paragraphs (one per line, format: "[INDEX] paragraph text"). Identify each exhibit (typically labeled "EXHIBIT A", "EXHIBIT B", "EXHIBIT D", etc., often with subtitles like "SCOPE OF WORK" or "BUDGET DETAIL AND PAYMENT PROVISIONS") and return the paragraph index range of each.

Return ONLY a JSON object — no preamble, no markdown, no code fences:
{
  "slices": [
    {
      "exhibit": "A" | "B" | "C" | "D" | "E",
      "suggested_title": "<short descriptive title for this exhibit, e.g. 'Scope of Work — Professional Services'>",
      "page_start": <0-indexed paragraph number where this exhibit's heading begins>,
      "page_end": <0-indexed paragraph number where this exhibit's last content paragraph is>,
      "confidence": "high" | "medium" | "low"
    }
  ]
}

(Note: the JSON keys are page_start/page_end for consistency, but the VALUES are PARAGRAPH INDICES — the [INDEX] number shown in the input.)

Rules:
1. Paragraph indices are 0-based and use the EXACT numbers shown in brackets in the input.
2. An exhibit's page_end is the last paragraph index that belongs to it (inclusive). If Exhibit B starts at paragraph 47 and Exhibit D starts at paragraph 89, then Exhibit B's page_end is 88 (or earlier if there's a clear boundary).
3. If you see a "Standard Agreement" (STD 213) section before any exhibits, do NOT include it as a slice. Only include exhibits with explicit "EXHIBIT X" labels.
4. Skip front matter (table of contents, signature pages, transmittal letters).
5. Confidence: "high" when boundaries are clear, "medium" with some ambiguity, "low" if guessing.
6. Order slices by page_start ascending.

If no identifiable exhibits, return { "slices": [] }.`;

    const userText = `Total paragraphs in document: ${totalCount}\nFile: ${fileName}\n\nNumbered paragraphs:\n\n${lines}\n\nIdentify all exhibits and return their paragraph index ranges per the instructions. Return JSON only.`;

    const raw = await callAI(apiKey, {
      system: sys,
      model: 'claude-sonnet-4-6',
      max_tokens: 1500,
      messages: [{ role: 'user', content: [{ type: 'text', text: userText }] }],
      tag: 'slicer_detect_docx',
    });

    const cleaned = String(raw || '').trim().replace(/^```(?:json)?\s*/i, '').replace(/```\s*$/i, '').trim();
    let parsed;
    try {
      parsed = JSON.parse(cleaned);
    } catch (e) {
      console.error('Slicer docx detection JSON parse failed:', e, 'raw:', cleaned);
      throw new Error('Penny returned an unparseable response. Try a different file.');
    }
    if (!Array.isArray(parsed?.slices) || parsed.slices.length === 0) {
      throw new Error('No exhibits detected. Check that the document has explicit "EXHIBIT X" headings.');
    }
    return parsed.slices.map((s, i) => ({
      id: 'slice-' + i,
      exhibit: String(s.exhibit || '').toUpperCase().replace(/[^A-E]/g, ''),
      suggested_title: String(s.suggested_title || ''),
      start: parseInt(s.page_start, 10) || 0,  // 0-indexed paragraphs
      end: parseInt(s.page_end, 10) || 0,
      confidence: ['high', 'medium', 'low'].includes(String(s.confidence)) ? String(s.confidence) : 'medium',
      include: true,
    })).filter(s => s.exhibit);
  }

  // Helper to mutate a single slice in the review list
  function updateSlice(idx, field, value) {
    setSlicerSlices(prev => prev.map((s, i) => i === idx ? { ...s, [field]: value } : s));
  }

  // Save each included slice as a separate template row
  async function handleSlicerSave() {
    const toSave = slicerSlices.filter(s => s.include);
    if (toSave.length === 0) {
      toast.warn('No slices selected to save');
      return;
    }
    if (!slicerFile?.base64) {
      toast.error('Source file missing — re-upload and try again');
      return;
    }
    setSlicerStep('saving');
    try {
      const newEntries = [];
      const isPdf = slicerFile.kind === 'pdf';
      const rangeLabel = isPdf ? 'pages' : 'paragraphs';
      for (const slice of toSave) {
        // Stored range structure depends on file kind. Both go in the same
        // exhibit_page_ranges field for now — consumers check the slice_type.
        // For docx, indices are paragraph indices in the document.xml; the
        // consumer can re-extract the file and pull paragraphs [start..end].
        const rangeStore = isPdf
          ? { [slice.exhibit]: { start: slice.start, end: slice.end, type: 'pages' } }
          : { [slice.exhibit]: { start: slice.start, end: slice.end, type: 'paragraphs' } };

        const entry = {
          id: uid(),
          name: slicerSourceName + ' — Exhibit ' + slice.exhibit + (slice.suggested_title ? ' (' + slice.suggested_title + ')' : ''),
          type: slicerContractType,
          exhibits: [slice.exhibit],
          notes: 'Sliced from "' + slicerFile.name + '" (' + rangeLabel + ' ' + slice.start + '–' + slice.end + '). Penny confidence: ' + slice.confidence + '.',
          fileName: slicerFile.name,
          fileSize: slicerFile.size,
          createdAt: today(),
          exhibit_page_ranges: rangeStore,
        };
        const ok = await templatesDb.save(entry, slicerFile.base64);
        if (!ok) throw new Error('Save failed for ' + entry.name);
        newEntries.push(entry);
      }
      onUpdate([...newEntries, ...templates]);
      toast.success(newEntries.length + ' template' + (newEntries.length === 1 ? '' : 's') + ' added to library');
      closeSlicer();
    } catch (err) {
      console.error('Slicer save error:', err);
      toast.error('Save failed: ' + err.message);
      setSlicerStep('review');
    }
  }

  return (
    <div>
      <div className="page-header">
        <div className="page-header-left">
          <h1 className="page-title">Contract Template Library</h1>
          <p className="page-subtitle">
            Upload sample contracts as drafting guides — Penny uses them to draft Exhibit B (Budget Detail & Payment Provisions) and Exhibit D (Special Terms and Conditions)
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <div className="tooltip-wrap" style={{ position: 'relative', display: 'inline-block' }}>
            <button className="btn btn-gold" onClick={openSlicer}>
              ✂️ Slice Multi-Exhibit Doc
            </button>
            <span className="tooltip-bubble" style={{
              position: 'absolute', top: 'calc(100% + 8px)', right: 0, zIndex: 50,
              width: 280, padding: '10px 12px', borderRadius: 8,
              background: 'var(--navy-900, #1a2744)', color: '#fff',
              fontSize: 11.5, lineHeight: 1.5, fontWeight: 400,
              boxShadow: '0 8px 24px rgba(0,0,0,0.25)',
              opacity: 0, visibility: 'hidden', transition: 'opacity .15s, visibility .15s',
              pointerEvents: 'none',
            }}>
              For files with multiple exhibits (A + B + D combined). Penny detects each exhibit's boundaries and creates a separate entry for each. Required if you want to mark an exhibit as canonical.
            </span>
          </div>
          <div className="tooltip-wrap" style={{ position: 'relative', display: 'inline-block' }}>
            <button className="btn btn-gold" onClick={() => { setEditing(null); setShowModal(true); }}>
              + Add Single Exhibit
            </button>
            <span className="tooltip-bubble" style={{
              position: 'absolute', top: 'calc(100% + 8px)', right: 0, zIndex: 50,
              width: 280, padding: '10px 12px', borderRadius: 8,
              background: 'var(--navy-900, #1a2744)', color: '#fff',
              fontSize: 11.5, lineHeight: 1.5, fontWeight: 400,
              boxShadow: '0 8px 24px rgba(0,0,0,0.25)',
              opacity: 0, visibility: 'hidden', transition: 'opacity .15s, visibility .15s',
              pointerEvents: 'none',
            }}>
              For single-exhibit files, or multi-exhibit files you want to keep as one combined reference. Faster, but multi-exhibit entries can't be canonical.
            </span>
          </div>
        </div>
      </div>

      {/* Type filter pills */}
      {templates.length > 0 && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 20 }}>
          <button className={`btn btn-xs ${filterType === 'All' ? 'btn-primary' : 'btn-ghost'}`} onClick={() => setFilterType('All')}>
            All ({templates.length})
          </button>
          {CONTRACT_TYPES.filter(t => typeCount[t] > 0).map(t => (
            <button key={t} className={`btn btn-xs ${filterType === t ? 'btn-primary' : 'btn-ghost'}`} onClick={() => setFilterType(t)}>
              {t} ({typeCount[t]})
            </button>
          ))}
        </div>
      )}

      {templates.length === 0 ? (
        <div className="card">
          <EmptyState
            icon="📂"
            title="No templates yet"
            subtitle="Upload sample contracts to give Penny real examples to follow. The more examples you add, the more accurate and consistent her Exhibit B and D drafts will be."
            action={
              <button className="btn btn-gold" onClick={() => setShowModal(true)}>
                + Add First Template
              </button>
            }
          />
        </div>
      ) : (
        <>
          {/* Stats row */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 20 }}>
            <div className="stat-card accent-navy">
              <div className="stat-label">Templates</div>
              <div className="stat-value" style={{ fontSize: 32 }}>{templates.length}</div>
              <div className="stat-sub">total uploaded</div>
            </div>
            <div className="stat-card accent-gold">
              <div className="stat-label">With Ex. B</div>
              <div className="stat-value" style={{ fontSize: 32 }}>{templates.filter(t => t.exhibits?.includes('B')).length}</div>
              <div className="stat-sub">budget & payment guides</div>
            </div>
            <div className="stat-card accent-green">
              <div className="stat-label">With Ex. D</div>
              <div className="stat-value" style={{ fontSize: 32 }}>{templates.filter(t => t.exhibits?.includes('D')).length}</div>
              <div className="stat-sub">special terms guides</div>
            </div>
            <div className="stat-card">
              <div className="stat-label">Contract Types</div>
              <div className="stat-value" style={{ fontSize: 32 }}>{Object.values(typeCount).filter(n => n > 0).length}</div>
              <div className="stat-sub">categories covered</div>
            </div>
          </div>

          {/* Template cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 14 }}>
            {filtered.map(t => {
              const isCanonical = t.reference_kind === 'canonical';
              return (
              <div key={t.id} className="card" style={{
                marginBottom: 0,
                borderLeft: isCanonical ? '4px solid #c49922' : '3px solid var(--navy-500)',
                background: isCanonical ? 'linear-gradient(to right, rgba(196,153,34,0.04), transparent 40%)' : undefined,
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--navy-800)', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
                      {isCanonical && <span title="Canonical — agency's blessed standard for this contract type">👑</span>}
                      {t.name}
                    </div>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                      {isCanonical && (
                        <span className="badge" style={{ fontSize: 9.5, background: '#fefce8', color: '#854d0e', border: '1px solid #fde68a' }}>
                          CANONICAL{t.exhibits?.length === 1 ? ' · EX. ' + t.exhibits[0] : ''}
                        </span>
                      )}
                      <span className="badge badge-navy" style={{ fontSize: 9.5 }}>{t.type}</span>
                      {(t.exhibits || []).map(ex => (
                        <span key={ex} className="badge badge-gold" style={{ fontSize: 9.5 }}>Ex. {ex}</span>
                      ))}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                    <button className="btn btn-xs btn-ghost" onClick={() => { setEditing(t); setShowModal(true); }}>Edit</button>
                    <button className="btn btn-xs btn-danger" onClick={() => handleRemove(t.id)}>✕</button>
                  </div>
                </div>
                <div style={{ fontSize: 11.5, color: 'var(--slate-500)' }}>
                  {t.fileName} · {t.fileSize ? fmtSize(t.fileSize) : ''}
                </div>
                {t.notes && (
                  <div style={{ fontSize: 11.5, color: 'var(--slate-500)', marginTop: 6, fontStyle: 'italic' }}>
                    {t.notes}
                  </div>
                )}
                <div style={{ fontSize: 10.5, color: 'var(--slate-400)', marginTop: 6 }}>
                  Added {fmtDate(t.createdAt)}{t.updatedAt ? ' · Updated ' + fmtDate(t.updatedAt) : ''}
                </div>
              </div>
              );
            })}
          </div>
        </>
      )}

      {showModal && (
        <TemplateModal
          initial={editing || {}}
          onSave={handleSave}
          onCancel={() => { setShowModal(false); setEditing(null); }}
          loading={saving}
          isAgencyAdmin={isAgencyAdmin}
        />
      )}

      {slicerStep && (
        <SlicerWizard
          step={slicerStep}
          file={slicerFile}
          slices={slicerSlices}
          sourceName={slicerSourceName}
          contractType={slicerContractType}
          onChangeSourceName={setSlicerSourceName}
          onChangeContractType={setSlicerContractType}
          onFilePicked={handleSlicerFilePicked}
          onUpdateSlice={updateSlice}
          onSave={handleSlicerSave}
          onClose={closeSlicer}
        />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// SlicerWizard — modal wizard for slicing a multi-exhibit PDF into
// separate template entries. Penny detects exhibit boundaries; user
// reviews, edits, deselects, and saves.
// ─────────────────────────────────────────────────────────────────────
function SlicerWizard({ step, file, slices, sourceName, contractType, onChangeSourceName, onChangeContractType, onFilePicked, onUpdateSlice, onSave, onClose }) {
  const isPdf = file?.kind === 'pdf';
  const rangeLabel = isPdf ? 'page' : 'paragraph';
  const rangeLabelPlural = isPdf ? 'pages' : 'paragraphs';
  return (
    <Modal
      title="Slice Multi-Exhibit Document"
      subtitle="Penny will detect exhibit boundaries (Exhibit A, B, D, etc.) and create a separate template for each"
      onClose={onClose}
      size="lg"
    >
      {step === 'upload' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ fontSize: 13, color: 'var(--slate-600)', lineHeight: 1.55 }}>
            Drop a PDF or Word document that contains multiple exhibits (e.g. a complete contract with Exhibit A, B, D combined). Penny reads it, detects each exhibit's boundaries, and creates a separate template entry for each. Each entry can then be used by the per-exhibit Penny flows (Term Review, Exhibit B/D templates, SOW Review).
          </div>
          <label
            className="upload-zone"
            style={{ cursor: 'pointer', textAlign: 'center', padding: 40, border: '2px dashed var(--slate-300)', borderRadius: 'var(--r-md)' }}
          >
            <input type="file" accept="application/pdf,.pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,.docx"
              onChange={e => { const f = e.target.files?.[0]; if (f) onFilePicked(f); }}
              style={{ display: 'none' }} />
            <div className="upload-zone-title" style={{ fontSize: 14, fontWeight: 600, color: 'var(--navy-700)' }}>
              Click or drag a multi-exhibit PDF or .docx here
            </div>
            <div className="upload-zone-sub" style={{ fontSize: 12, color: 'var(--slate-500)', marginTop: 6 }}>
              Penny will detect exhibit boundaries automatically
            </div>
          </label>
          <div style={{ fontSize: 11, color: 'var(--slate-400)', lineHeight: 1.5 }}>
            <strong>Tip:</strong> Works best with documents that have clear "EXHIBIT A", "EXHIBIT B", etc. headings. For PDFs: scanned PDFs without text won't work. For Word: exhibit boundaries are detected by paragraph rather than page.
          </div>
        </div>
      )}

      {step === 'detect' && (
        <div style={{ textAlign: 'center', padding: 60, color: 'var(--slate-500)' }}>
          <div style={{ fontSize: 32, marginBottom: 12 }}>🔍</div>
          <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--navy-700)' }}>Penny is reading "{file?.name}"…</div>
          <div style={{ fontSize: 12, color: 'var(--slate-400)', marginTop: 8 }}>Detecting exhibit boundaries — usually 15–30 seconds.</div>
        </div>
      )}

      {step === 'review' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ fontSize: 12, color: 'var(--slate-600)', lineHeight: 1.5 }}>
            Penny detected <strong>{slices.length}</strong> exhibit{slices.length === 1 ? '' : 's'} in <strong>{file?.name}</strong>. Review below — adjust {rangeLabelPlural} ranges if needed, uncheck any you don't want to save, then click Save.
          </div>

          {/* Source name + contract type — applied to all slices */}
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 10 }}>
            <div className="form-field">
              <label style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--slate-500)' }}>Base name (used in each template title)</label>
              <input type="text" value={sourceName} onChange={e => onChangeSourceName(e.target.value)}
                placeholder="e.g., CPS HR Consulting 2024" />
            </div>
            <div className="form-field">
              <label style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--slate-500)' }}>Contract type</label>
              <select value={contractType} onChange={e => onChangeContractType(e.target.value)}>
                {CONTRACT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
          </div>

          {/* Slice list */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {slices.map((s, i) => {
              const confColor = s.confidence === 'high' ? '#16a34a' : s.confidence === 'low' ? '#dc2626' : '#0284c7';
              const confBg    = s.confidence === 'high' ? '#dcfce7' : s.confidence === 'low' ? '#fee2e2' : '#e0f2fe';
              return (
                <div key={s.id} style={{
                  padding: 12, background: s.include ? 'var(--white)' : 'var(--slate-100)',
                  border: '1.5px solid ' + (s.include ? 'var(--slate-200)' : 'var(--slate-200)'),
                  borderRadius: 8, opacity: s.include ? 1 : 0.55,
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                    <input type="checkbox" checked={s.include} onChange={e => onUpdateSlice(i, 'include', e.target.checked)}
                      style={{ width: 16, height: 16, cursor: 'pointer' }} />
                    <span style={{
                      fontSize: 12, fontWeight: 700, padding: '3px 10px', borderRadius: 99,
                      background: 'var(--navy-50)', color: 'var(--navy-700)',
                    }}>Exhibit {s.exhibit}</span>
                    <span style={{
                      fontSize: 10.5, fontWeight: 700, padding: '3px 8px', borderRadius: 99,
                      background: confBg, color: confColor, letterSpacing: '.04em',
                    }}>{s.confidence.toUpperCase()}</span>
                    <span style={{ fontSize: 12, color: 'var(--slate-500)', flex: 1 }}>{s.suggested_title || '—'}</span>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                    <div>
                      <label style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', color: 'var(--slate-500)' }}>{rangeLabel} start</label>
                      <input type="number" min={isPdf ? 1 : 0} value={s.start}
                        disabled={!s.include}
                        onChange={e => onUpdateSlice(i, 'start', parseInt(e.target.value, 10) || (isPdf ? 1 : 0))}
                        style={{ width: '100%', padding: '5px 8px', borderRadius: 5, border: '1px solid var(--slate-200)', fontSize: 12 }} />
                    </div>
                    <div>
                      <label style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', color: 'var(--slate-500)' }}>{rangeLabel} end</label>
                      <input type="number" min={isPdf ? 1 : 0} value={s.end}
                        disabled={!s.include}
                        onChange={e => onUpdateSlice(i, 'end', parseInt(e.target.value, 10) || (isPdf ? 1 : 0))}
                        style={{ width: '100%', padding: '5px 8px', borderRadius: 5, border: '1px solid var(--slate-200)', fontSize: 12 }} />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div style={{ fontSize: 10.5, color: 'var(--slate-500)', padding: 8, background: 'var(--slate-50)', borderRadius: 5, lineHeight: 1.5 }}>
            Note: each saved slice carries a copy of the full source file. {isPdf ? 'Page' : 'Paragraph'} ranges are stored so Penny can focus on the relevant {rangeLabelPlural} when she references the template later. The file is not physically split.
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
            <button className="btn btn-gold" onClick={onSave} disabled={slices.filter(s => s.include).length === 0}>
              💾 Save {slices.filter(s => s.include).length} template{slices.filter(s => s.include).length === 1 ? '' : 's'}
            </button>
          </div>
        </div>
      )}

      {step === 'saving' && (
        <div style={{ textAlign: 'center', padding: 60, color: 'var(--slate-500)' }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--navy-700)' }}>Saving templates…</div>
        </div>
      )}
    </Modal>
  );
}
